import { definicaoDaFuncao, type FuncaoCorda } from './funcoesCorda'
import { contornosDaCorda } from './contornoCorda'
import { folgaDoTrecho } from './folgaCorda'
import { localizarPontoNoMundo, pontosDeAncoragem } from './ancoragem'
import { gerarIdPadrao } from './operacoes'
import type {
  CenaTabletop,
  CordaTabletop,
  GeradorIdTabletop,
  ReferenciaPonto,
  SentidoCorda,
  VetorTabletop,
} from './tipos'

/**
 * Cordas do table top: criar, editar, apagar e calcular onde elas estão.
 *
 * A corda guarda `objeto + ponto` (ver `ancoragem.ts`), nunca coordenadas. As
 * posições e o comprimento são sempre calculados a partir da cena atual, então
 * mover a viatura ou mudar o ângulo da escada leva a corda junto sem nenhum
 * cuidado extra. Se o ponto deixa de existir (a escada ficou mais curta e o
 * degrau 18 sumiu), a corda NÃO some: fica com uma ponta solta, que o painel
 * mostra, e o instrutor decide o que fazer. Já remover um objeto apaga as
 * cordas que passam por ele (ver `removerObjeto`).
 *
 * TypeScript puro: sem React e sem Three.js. A física da corda (juntas, tensão)
 * é da fase de simulação; aqui os trechos são retos.
 */

export type NovaCorda = Readonly<{
  funcao: FuncaoCorda
  percurso: readonly ReferenciaPonto[]
  /** Sem isto, a função decide: a tração nasce com a seta para o fim, as demais sem seta. */
  sentido?: SentidoCorda
  rotulo?: string
}>

export type AtualizacoesCorda = Readonly<{
  funcao?: FuncaoCorda
  /** `null` tira a seta. */
  sentido?: SentidoCorda | null
  /** Vazio (ou só espaços) tira o rótulo. */
  rotulo?: string
}>

const mesmaReferencia = (a: ReferenciaPonto, b: ReferenciaPonto) => a.idObjeto === b.idObjeto && a.idPonto === b.idPonto

const copiarPercurso = (percurso: readonly ReferenciaPonto[]): ReferenciaPonto[] =>
  percurso.map((p) => ({ idObjeto: p.idObjeto, idPonto: p.idPonto }))

function validarPercurso(cena: CenaTabletop, percurso: readonly ReferenciaPonto[]): void {
  if (percurso.length < 2) throw new Error('Uma corda precisa de dois pontos ou mais.')
  percurso.forEach((referencia, i) => {
    const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
    if (!objeto) throw new Error(`A corda passa pelo objeto "${referencia.idObjeto}", que não existe na cena.`)
    if (!pontosDeAncoragem(objeto).some((p) => p.id === referencia.idPonto)) {
      throw new Error(`O objeto "${objeto.nome}" não tem o ponto de ancoragem "${referencia.idPonto}".`)
    }
    const anterior = percurso[i - 1]
    if (anterior && mesmaReferencia(anterior, referencia)) {
      throw new Error('A corda não pode passar duas vezes seguidas pelo mesmo ponto.')
    }
  })
}

export function adicionarCorda(cena: CenaTabletop, nova: NovaCorda, gerarId: GeradorIdTabletop = gerarIdPadrao): CenaTabletop {
  validarPercurso(cena, nova.percurso)
  const id = gerarId()
  if ((cena.cordas ?? []).some((c) => c.id === id)) throw new Error(`Já existe uma corda com o ID "${id}".`)
  const sentido = nova.sentido ?? (definicaoDaFuncao(nova.funcao).nascemComSeta ? 'paraFim' : undefined)
  const rotulo = nova.rotulo?.trim()
  const corda: CordaTabletop = {
    id,
    funcao: nova.funcao,
    percurso: copiarPercurso(nova.percurso),
    ...(sentido ? { sentido } : {}),
    ...(rotulo ? { rotulo } : {}),
  }
  return { ...cena, cordas: [...(cena.cordas ?? []), corda] }
}

function mapearCorda(cena: CenaTabletop, id: string, alterar: (corda: CordaTabletop) => CordaTabletop): CenaTabletop {
  if (!cena.cordas?.some((c) => c.id === id)) return cena
  return { ...cena, cordas: cena.cordas.map((c) => (c.id === id ? alterar(c) : c)) }
}

export function atualizarCorda(cena: CenaTabletop, id: string, alteracoes: AtualizacoesCorda): CenaTabletop {
  return mapearCorda(cena, id, (corda) => {
    const { sentido: _s, rotulo: _r, ...resto } = corda
    const sentido = alteracoes.sentido === undefined ? corda.sentido : (alteracoes.sentido ?? undefined)
    const rotuloBruto = alteracoes.rotulo === undefined ? corda.rotulo : alteracoes.rotulo.trim()
    return {
      ...resto,
      ...(alteracoes.funcao ? { funcao: alteracoes.funcao } : {}),
      ...(sentido ? { sentido } : {}),
      ...(rotuloBruto ? { rotulo: rotuloBruto } : {}),
    }
  })
}

/**
 * Muda a ponta da corda (o início ou o fim) para outro ponto de ancoragem,
 * sem apagar e refazer a corda: a função, a seta e as folgas ficam. Falha
 * (como `adicionarCorda`) se o ponto não existe ou repete o vizinho.
 */
export function moverPontaDaCorda(cena: CenaTabletop, id: string, lado: 'inicio' | 'fim', referencia: ReferenciaPonto): CenaTabletop {
  const corda = cena.cordas?.find((c) => c.id === id)
  if (!corda) return cena
  return moverPontoDoPercurso(cena, id, lado === 'inicio' ? 0 : corda.percurso.length - 1, referencia)
}

/**
 * Leva um ponto qualquer do percurso (uma ponta ou um ponto do meio) para outro
 * ponto de ancoragem. Falha como `adicionarCorda` se o ponto não existe ou repete o vizinho.
 */
export function moverPontoDoPercurso(cena: CenaTabletop, id: string, indice: number, referencia: ReferenciaPonto): CenaTabletop {
  const corda = cena.cordas?.find((c) => c.id === id)
  if (!corda || indice < 0 || indice >= corda.percurso.length) return cena
  const percurso = copiarPercurso(corda.percurso)
  percurso[indice] = { idObjeto: referencia.idObjeto, idPonto: referencia.idPonto }
  validarPercurso(cena, percurso)
  return mapearCorda(cena, id, (c) => ({ ...c, percurso }))
}

/** Inverte a seta: fim vira início e vice-versa; sem seta, passa a apontar para o fim. */
export function inverterSentidoCorda(cena: CenaTabletop, id: string): CenaTabletop {
  return mapearCorda(cena, id, (corda) => ({ ...corda, sentido: corda.sentido === 'paraFim' ? 'paraInicio' : 'paraFim' }))
}

export function removerCorda(cena: CenaTabletop, id: string): CenaTabletop {
  if (!cena.cordas?.some((c) => c.id === id)) return cena
  return { ...cena, cordas: cena.cordas.filter((c) => c.id !== id) }
}

export const cordasDoObjeto = (cena: CenaTabletop, idObjeto: string): readonly CordaTabletop[] =>
  (cena.cordas ?? []).filter((c) => c.percurso.some((p) => p.idObjeto === idObjeto))

/** Onde cada ponto da corda está agora; `null` no ponto que não existe mais. */
export const percursoNoMundo = (cena: CenaTabletop, corda: CordaTabletop): ReadonlyArray<VetorTabletop | null> =>
  corda.percurso.map((referencia) => localizarPontoNoMundo(cena, referencia))

/** Pontos da corda que não existem mais (o objeto mudou de forma). */
export const pontasSoltas = (cena: CenaTabletop, corda: CordaTabletop): readonly ReferenciaPonto[] =>
  corda.percurso.filter((referencia) => localizarPontoNoMundo(cena, referencia) === null)

/** Comprimento aproximado, em metros: a soma dos trechos retos entre pontos que existem. */
export function comprimentoDaCorda(cena: CenaTabletop, corda: CordaTabletop): number {
  const pontos = percursoNoMundo(cena, corda)
  const contornos = contornosDaCorda(cena, corda, pontos)
  let total = 0
  for (let i = 1; i < pontos.length; i += 1) {
    const a = pontos[i - 1]
    const b = pontos[i]
    if (!a || !b) continue
    const folga = folgaDoTrecho(corda, i - 1)
    // esticado, o trecho contorna os volumes no caminho; frouxo, vale a reta mais a folga
    const caminho = folga > 0 ? [a, b] : [a, ...(contornos[i - 1]?.intermediarios ?? []), b]
    for (let k = 1; k < caminho.length; k += 1) {
      const p = caminho[k - 1]!
      const q = caminho[k]!
      total += Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2])
    }
    total += folga
  }
  return total
}
